import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { compose, pure } from 'recompose';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import styled from 'styled-components';
import { Field } from 'react-final-form';

import { WithUser } from 'hocs';
import { getBridgeFirstStepRefs, getEntitiesCollectionWithEmptyItem, getIsInIframe } from 'selectors';

import { Link } from 'components/shared';
import {
  Condition,
  FormAsterisk,
  FormBox,
  FormGroupAdapter,
  FormLegend,
  NarrowFormFlex,
} from 'components/shared/form';
import HiddenTrackFields from 'components/blocks/LeadForm/shared/HiddenTrackFields';

import { LOAN_OFFICER_ID_CONDITION, REFERRED_BY_COMMENT_CONDITION } from 'constants/forms';
import { SIGN_IN_PATH } from 'constants/routes';
import { userDefaultProps, userPropTypes } from 'propTypes';
import { composeValidators, validateEmail, validatePhone, validateRequired } from 'utils';

const propTypes = {
  ...userPropTypes,
  isInIframe: PropTypes.bool.isRequired,
  refs: PropTypes.shape({
    statesCollection: PropTypes.arrayOf(PropTypes.object),
    entitiesCollection: PropTypes.arrayOf(PropTypes.object),
    referredByTypeCollection: PropTypes.arrayOf(PropTypes.object),
    loanOfficersCollection: PropTypes.arrayOf(PropTypes.object),
    creditScoresCollection: PropTypes.arrayOf(PropTypes.object),
  }),
  location: PropTypes.objectOf(PropTypes.any),
};

const defaultProps = {
  ...userDefaultProps,
  refs: {
    statesCollection: [],
    entitiesCollection: [],
    referredByTypeCollection: [],
    loanOfficersCollection: [],
    creditScoresCollection: [],
  },
};

function Fields({ refs, user, entitiesCollection, isInIframe, contact, location }) {
  return (
    <Fragment>
      <NarrowFormFlex alignItems="center" justifyContent="space-between">
        <FormBox mb="1rem" width={[1, 1, 'auto']}>
          <FormLegend>General Info</FormLegend>
        </FormBox>
        {!user && !isInIframe && (
          <FormBox mb="1rem" p={[3, 3, 0]} alignSelf={['flex-end', 'flex-end', 'inherit']}>
            <SignInLegend>
              Do you have a LendingOne account? Please{' '}
              <Link to={{ pathname: SIGN_IN_PATH, state: { from: location } }}>Sign In</Link>
            </SignInLegend>
          </FormBox>
        )}
      </NarrowFormFlex>
      <HiddenTrackFields />
      {!(contact && contact.firstName && contact.lastName) && (
        <NarrowFormFlex>
          <FormBox width={[1, 1, 1 / 2]}>
            <Field
              name="application.firstName"
              label="First Name"
              required
              validate={validateRequired()}
              component={FormGroupAdapter}
            />
          </FormBox>
          <FormBox width={[1, 1, 1 / 2]}>
            <Field
              name="application.lastName"
              label="Last Name"
              required
              validate={validateRequired()}
              component={FormGroupAdapter}
            />
          </FormBox>
        </NarrowFormFlex>
      )}
      {!(contact && contact.email && contact.phone) && (
        <NarrowFormFlex>
          <FormBox width={[1, 1, 5 / 12]}>
            <Field
              name="application.email"
              label="Email"
              type="email"
              required
              validate={composeValidators(validateRequired(), validateEmail())}
              component={FormGroupAdapter}
            />
          </FormBox>
          <FormBox width={[1, 1, 4 / 12]}>
            <Field
              name="application.phone"
              label="Phone"
              type="tel"
              required
              typeOfMask="phone"
              validate={composeValidators(validateRequired(), validatePhone())}
              component={FormGroupAdapter}
            />
          </FormBox>
          <FormBox width={[1, 1, 3 / 12]}>
            <Field
              name="application.phoneExtension"
              label="Extension#"
              typeOfMask="phoneExtension"
              component={FormGroupAdapter}
            />
          </FormBox>
        </NarrowFormFlex>
      )}
      {!(contact && contact.stateCode) && (
        <NarrowFormFlex>
          <FormBox width={1}>
            <Field
              name="application.stateCode"
              label="Your State"
              type="select"
              required
              options={refs.statesCollection}
              validate={validateRequired()}
              component={FormGroupAdapter}
            />
          </FormBox>
        </NarrowFormFlex>
      )}
      {!(contact && contact.investmentPropertiesCountSoldLast36Months) && (
        <NarrowFormFlex>
          <FormBox width={1}>
            <Field
              name="application.investmentPropertiesCountSoldLast36Months"
              label="Number of Fix/Flip or New Construction Properties Sold in the Last 36 Months"
              required
              validate={validateRequired()}
              component={FormGroupAdapter}
              typeOfMask="number"
            />
          </FormBox>
        </NarrowFormFlex>
      )}
      <NarrowFormFlex>
        <FormBox width={1}>
          <Field
            name="application.rentalPropertiesCount"
            label="Number of Rental Properties you Currently Own"
            component={FormGroupAdapter}
            typeOfMask="number"
          />
        </FormBox>
      </NarrowFormFlex>

      {!(contact && contact.creditScore) && (
        <NarrowFormFlex>
          <FormBox width={1}>
            <Field
              name="application.creditScore"
              label="Your Credit Score"
              type="select"
              required
              options={refs.creditScoresCollection}
              validate={validateRequired()}
              component={FormGroupAdapter}
            />
          </FormBox>
        </NarrowFormFlex>
      )}
      {user && entitiesCollection && (
        <NarrowFormFlex>
          <FormBox width={1}>
            <Field
              name="application.entityId"
              type="select"
              options={entitiesCollection}
              allowNull
              placeholder="New Entity"
              label="Entity"
              component={FormGroupAdapter}
            />
          </FormBox>
        </NarrowFormFlex>
      )}
      {!user && (
        <NarrowFormFlex>
          <FormBox width={[1, 1, 1 / 2]}>
            <Field
              name="application.referredByType"
              label="How did you find us?"
              type="select"
              options={refs.referredByTypeCollection}
              component={FormGroupAdapter}
            />
          </FormBox>

          <FormBox width={[1, 1, 1 / 2]}>
            <Condition when="application.referredByType" is={REFERRED_BY_COMMENT_CONDITION}>
              <div>
                <Field
                  name="application.referredByComment"
                  label="Name or Code"
                  component={FormGroupAdapter}
                  isConditional
                />
              </div>
            </Condition>

            <Condition when="application.referredByType" is={LOAN_OFFICER_ID_CONDITION}>
              <div>
                <Field
                  name="application.loanOfficerId"
                  type="select"
                  options={refs.loanOfficersCollection}
                  label="LendingOne Loan Officer"
                  component={FormGroupAdapter}
                  isConditional
                />
              </div>
            </Condition>
          </FormBox>
        </NarrowFormFlex>
      )}
      <NarrowFormFlex>
        <FormBox width={1}>
          <FormAsterisk>
            All red asterisks are required in order to provide you with an accurate rate quote.
          </FormAsterisk>
        </FormBox>
      </NarrowFormFlex>
    </Fragment>
  );
}

const SignInLegend = styled.div`
  display: block;
  font-size: var(--font-size-xs);
  color: var(--color-secondary-text);
  letter-spacing: 0px;
`;

Fields.propTypes = propTypes;
Fields.defaultProps = defaultProps;

function mapStateToProps(state) {
  return {
    entitiesCollection: getEntitiesCollectionWithEmptyItem(state),
    refs: getBridgeFirstStepRefs(state),
    isInIframe: getIsInIframe(state),
  };
}

const enhance = compose(
  withRouter,
  WithUser,
  connect(mapStateToProps),
  pure,
);

export default enhance(Fields);
